import { motion } from "framer-motion";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { flooringTypes, FlooringTypeData } from "@/lib/validation";

interface FlooringTypeStepProps {
  data: FlooringTypeData;
  onDataChange: (data: FlooringTypeData) => void;
  errors?: string[];
}

const flooringIcons: Record<string, string> = {
  Hardwood: "🪵",
  Vinyl: "💧",
  Carpet: "🧶",
  Tile: "🔲",
  Laminate: "📋",
  "Not Sure": "❓",
};

const FlooringTypeStep = ({
  data,
  onDataChange,
  errors,
}: FlooringTypeStepProps) => {
  const toggleType = (type: (typeof flooringTypes)[number]) => {
    const newTypes = data.flooringTypes.includes(type)
      ? data.flooringTypes.filter((t) => t !== type)
      : [...data.flooringTypes, type];
    onDataChange({ flooringTypes: newTypes as typeof data.flooringTypes });
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <h2 className="font-serif text-2xl md:text-3xl font-bold text-foreground mb-2">
          What type of flooring are you interested in?
        </h2>
        <p className="text-muted-foreground">
          Select all that apply. We'll help you explore the best options.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {flooringTypes.map((type) => {
          const isSelected = data.flooringTypes.includes(type);
          return (
            <motion.div
              key={type}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <label
                className={`flex items-center gap-4 p-5 rounded-xl border-2 cursor-pointer transition-all ${
                  isSelected
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggleType(type)}
                  className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                />
                <span className="text-2xl">{flooringIcons[type]}</span>
                <span className="text-foreground font-medium">{type}</span>
              </label>
            </motion.div>
          );
        })}
      </div>

      {errors && errors.length > 0 && (
        <p className="text-destructive text-sm text-center mt-4">
          {errors[0]}
        </p>
      )}
    </motion.div>
  );
};

export default FlooringTypeStep;
